import { useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Shell, landingSection, mayOpen, type Section } from './components/Shell';
import { Login } from './components/Login';
import { Home, type HomeTab } from './sections/Home';
import { Studies } from './sections/Studies';
import { FormsSubmission, type FormsTab } from './sections/FormsSubmission';
import { Participants, NoBoards } from './sections/Participants';
import { INBOX, Messages, type MessageView } from './sections/Messages';
import { Contacts } from './sections/Contacts';
import { Tickets, type TicketsTab } from './sections/Tickets';
import { Meetings } from './sections/Meetings';
import { Redcap, type RedcapTab, type RedcapTarget } from './sections/Redcap';
import { Roles } from './sections/Roles';
import { Users } from './sections/Users';
import { Newsletter } from './sections/Newsletter';
import { Account } from './sections/Account';
import { SubmissionDetail } from './sections/SubmissionDetail';
import { SetPassword } from './components/SetPassword';
import { ChooseFirstPassword } from './components/ChooseFirstPassword';
import { Loading } from './components/ui';
import { useMeta, useOverview } from './api/queries';
import { useNotificationFeed, type NotificationEvent } from './api/notifications';
import { Toasts } from './components/Toasts';
import { ApiError } from './api/client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry an auth or permission failure — the client already tried
        // a refresh, and hammering a 403 just fills the audit log.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/** The four forms that land in the Forms submission inbox, by tab key. */
const FORMS_TABS: readonly FormsTab[] = ['intake', 'referral', 'waitlist', 'early-interest'];

/**
 * Which section shows a given form.
 *
 * Mirrors `/meta`'s formGroups: participant pathways arrive in the Forms
 * submission inbox (a board is only for what has been accepted into a study),
 * general inquiries have their own screen, and everything else is a request.
 */
const SECTION_FOR_FORM: Record<string, Section> = {
  intake: 'forms',
  referral: 'forms',
  waitlist: 'forms',
  'early-interest': 'forms',
  inquiry: 'messages',
};

/** The sidebar entry each Tickets page lives under, so the highlight follows the page. */
const TICKETS_SECTION: Record<TicketsTab, Section> = {
  mine: 'tickets',
  new: 'ticket-new',
};

function Dashboard() {
  const { can } = useAuth();
  /**
   * The section asked for, and the one shown. They differ only when the role
   * may not open what was asked — a section reached some other way than the
   * sidebar (a notification, a board's REDCap link, a meeting link from
   * Tickets), or one whose permission was taken away mid-session — and then
   * the role's own landing section shows instead of a screen that would 403.
   */
  const [requested, setSection] = useState<Section>(() => landingSection(can));
  const section: Section = mayOpen(requested, can) ? requested : landingSection(can);
  /** Which study's board Participants shows. Every board is one study's. */
  const [study, setStudy] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** A record opened from the bell or a toast, on top of whatever section is showing. */
  const [openRecord, setOpenRecord] = useState<string | null>(null);
  /**
   * Which type Messages shows, and which tab the other tabbed sections are
   * showing. Lifted so a notification can land on the right one.
   */
  const [messageView, setMessageView] = useState<MessageView>('all');
  const [inboxThread, setInboxThread] = useState<string | null>(null);
  const [formsTab, setFormsTab] = useState<FormsTab>('all');
  const [homeTab, setHomeTab] = useState<HomeTab>('overview');
  /** Where a study's board asked the REDCap section to open. */
  const [redcapTarget, setRedcapTarget] = useState<RedcapTarget | null>(null);
  /** Which Tickets page is showing: the sidebar has My tickets and New ticket as entries of their own. */
  const [ticketsTab, setTicketsTab] = useState<TicketsTab>('mine');
  // Bumped by the sidebar's My tickets entry so it lands on the list even with a ticket's page open.
  const [ticketsNonce, setTicketsNonce] = useState(0);
  /** A ticket opened from the bell or a toast: My tickets lands on its page rather than the list. */
  const [ticketToOpen, setTicketToOpen] = useState<string | null>(null);
  // Badge counts, for the roles that can open what they count.
  const seesSubmissions = can('submissions:read');
  const overview = useOverview(seesSubmissions);
  const meta = useMeta();

  /**
   * How many unactioned rows each section is holding.
   *
   * Built from the per-form `fresh` counts the overview already returns —
   * rows still waiting in the inbox (received, waitlist or early interest),
   * so a waitlist sign-up counts although it never passes through Received —
   * and grouped with `/meta`'s own formGroups so the split cannot drift from
   * the API's. The previous version showed `stageCounts.new` — a total across all
   * forms — on the Inquiries item alone, so the badge said 4 while the table
   * said 1.
   */
  const newCounts = useMemo<Partial<Record<Section, number>>>(() => {
    const stats = overview.data?.formStats ?? [];
    const groups = meta.data?.formGroups;
    const freshFor = (keys: readonly string[]) =>
      stats.filter((f) => keys.includes(f.key)).reduce((n, f) => n + f.fresh, 0);

    const participants = groups?.participant ?? ['intake', 'referral', 'waitlist', 'early-interest'];
    const requests = groups?.requests ?? [];

    // Zero is not a badge — `NavButton` hides anything falsy, and a grey "0"
    // beside every section is noise pretending to be information.
    const counts: Partial<Record<Section, number>> = {
      // New participant forms are inbox work, so the count sits on the inbox.
      forms: freshFor(participants),
      // One entry now, so one badge: general inquiries and requests together.
      messages: freshFor(['inquiry']) + freshFor(requests),
    };
    return counts;
  }, [overview.data, meta.data]);

  // One poll for the whole app: the bell and the toasts read the same feed,
  // so opening the dashboard does not start two timers against the API.
  // Only for a role that may read what it announces: records and the inbox
  // need submissions:read, a ticket given to you needs tickets:read (a
  // tickets-only account hears only of those), and a role with neither would
  // poll a 403 every 30 seconds.
  const feed = useNotificationFeed(seesSubmissions || can('tickets:read'));

  /**
   * Open the record a notification is about.
   *
   * Both: the section switches underneath so closing the record leaves you on
   * the right list, and the record itself opens on top. The notification id is
   * the submission id, so nothing has to be looked up.
   *
   * Opening a detail view is an audited PHI access, and it still is one here —
   * what makes it legitimate is the operator's click, not which control they
   * clicked. Nothing opens on its own; a toast that arrives and is ignored
   * reads no record.
   */
  const openEvent = (event: NotificationEvent) => {
    setSearch('');
    if (event.type === 'ticket') {
      // A ticket someone just gave you: its own page under My tickets. Not a
      // record, so whatever record was open on top closes.
      if (!event.ticketId) return;
      setOpenRecord(null);
      setTicketsTab('mine');
      setTicketToOpen(event.ticketId);
      // A fresh Tickets, so it opens on this ticket even with another one showing.
      setTicketsNonce((n) => n + 1);
      setSection('tickets');
      return;
    }
    if (event.type === 'inbox') {
      // An email to enact@: its conversation in the Messages inbox.
      setSection('messages');
      setMessageView(INBOX);
      setInboxThread(event.threadId);
      return;
    }
    const target = SECTION_FOR_FORM[event.form] ?? 'messages';
    setSection(target);
    // Land on the record's own type, not whichever was last open.
    if (target === 'messages') setMessageView(event.form);
    if ((FORMS_TABS as readonly string[]).includes(event.form)) setFormsTab(event.form as FormsTab);
    setOpenRecord(event.submissionId);
  };

  function go(next: Section) {
    // The header search only means anything on the inquiries and requests tables.
    if (next !== 'messages' && next !== 'contacts') setSearch('');
    // A board is always one study's: landing on Participants with none picked
    // means the first one in the registry.
    if (next === 'participants' && !study) setStudy(meta.data?.studies[0]?.key ?? null);
    // From the menu, REDCap opens as it always has rather than where a board last sent it.
    if (next === 'redcap') setRedcapTarget(null);
    // The two Tickets entries are one screen on different pages.
    if (next === 'tickets') {
      setTicketsTab('mine');
      setTicketToOpen(null);
      setTicketsNonce((n) => n + 1);
    }
    if (next === 'ticket-new') setTicketsTab('new');
    setSection(next);
  }

  function openRedcap(key: string, tab: RedcapTab, recordId?: string) {
    setSearch('');
    setRedcapTarget({ study: key, tab, recordId, nonce: Date.now() });
    setSection('redcap');
  }

  /** A meeting's action item on the Tickets screen opens the meeting it was raised in. */
  function openMeeting(id: string) {
    try {
      sessionStorage.setItem('enact.meetings.open', id);
    } catch {
      /* a private window: the list opens instead */
    }
    go('meetings');
  }

  function goStudy(key: string) {
    setSearch('');
    setStudy(key);
    setSection('participants');
  }

  return (
    <Shell
      section={section}
      onSection={go}
      study={study}
      onStudy={goStudy}
      // A board is nine columns side by side; give it the whole window.
      wide={section === 'participants'}
      newCounts={newCounts}
      search={search}
      onSearch={(q) => {
        setSearch(q);
        if (q && section !== 'messages' && section !== 'contacts') setSection('messages');
      }}
      notifications={{
        events: feed.events,
        unreadCount: feed.unreadCount,
        markAllRead: feed.markAllRead,
        onOpen: openEvent,
      }}
    >
      {section === 'overview' && <Home tab={homeTab} onTab={setHomeTab} onSection={go} onOpenRedcap={openRedcap} />}
      {section === 'studies' && <Studies />}
      {section === 'forms' && <FormsSubmission tab={formsTab} onTab={setFormsTab} />}
      {section === 'participants' &&
        (study ? (
          <Participants key={study} study={study} onOpenRedcap={openRedcap} />
        ) : (
          <NoBoards />
        ))}
      {section === 'messages' && (
        <Messages
          view={messageView}
          onView={setMessageView}
          search={search}
          onSearch={setSearch}
          inboxThread={inboxThread}
          onInboxThread={setInboxThread}
        />
      )}
      {section === 'contacts' && <Contacts search={search} onSearch={setSearch} />}
      {(section === 'tickets' || section === 'ticket-new') && (
        <Tickets
          key={ticketsNonce}
          tab={ticketsTab}
          openTicket={ticketToOpen}
          onTab={(t) => {
            setTicketsTab(t);
            setSection(TICKETS_SECTION[t]);
          }}
          onOpenMeeting={openMeeting}
          onOpenRedcap={(study) => openRedcap(study, 'completeness')}
        />
      )}
      {section === 'meetings' && <Meetings />}
      {section === 'redcap' && (
        <Redcap target={redcapTarget} onOpenBoard={goStudy} onOpenMeeting={openMeeting} onGoMeetings={() => go('meetings')} />
      )}
      {section === 'roles' && <Roles />}
      {section === 'users' && <Users />}
      {section === 'newsletter' && <Newsletter />}
      {section === 'account' && <Account />}

      {openRecord && <SubmissionDetail id={openRecord} onClose={() => setOpenRecord(null)} />}

      <Toasts
        events={feed.toasts}
        onOpen={(e) => {
          feed.dismissToast(e.id);
          openEvent(e);
        }}
        onDismiss={feed.dismissToast}
      />
    </Shell>
  );
}

/**
 * `/set-password?token=…` is where the API's invite and reset emails land.
 *
 * Read from `location` rather than added to a router: the dashboard is one
 * screen with a section switcher, and this is the only URL it needs to
 * recognise. nginx answers every unknown path with index.html
 * (nginx.conf.template), so the link reaches the app; without this branch it reached the sign-in form instead,
 * which meant an invited admin could never set a password.
 */
function useResetToken(): string | null {
  // Read once on mount: the token is whatever the link carried, and nothing in
  // the app navigates away from it except the reload after redeeming.
  const [token] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (window.location.pathname !== '/set-password') return null;
    return new URLSearchParams(window.location.search).get('token');
  });
  return token;
}

function Gate() {
  const { admin, loading } = useAuth();
  const token = useResetToken();

  // Signed out, by hand or by a session that ended on its own (idle, revoked):
  // nothing the last session read stays in memory for whoever signs in next on
  // this browser. `/meta` is held forever and scoped to its reader, so the next
  // person would otherwise start with the last one's studies.
  useEffect(() => {
    if (!admin) queryClient.clear();
  }, [admin]);

  // Before the session check: someone following a reset link may already be
  // signed in on this device, and the link is what they came to act on.
  if (token) {
    return (
      <SetPassword
        token={token}
        onDone={() => {
          window.history.replaceState(null, '', '/');
          window.location.reload();
        }}
      />
    );
  }

  if (loading) return <Loading label="Checking your session…" />;
  if (!admin) return <Login />;
  // Signed in on an invite's temporary password: nothing else opens until
  // they choose their own (the API answers 403 to everything else).
  if (admin.mustChangePassword) return <ChooseFirstPassword />;
  return <Dashboard />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </QueryClientProvider>
  );
}
